import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';

import Analytics from '@web-solutions/module-analytics';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import { setEmail as setProfileEmail } from '@web-solutions/base-app/store/profile/actions';
import { initOrders } from '@web-solutions/core/store/billing/actions';

import { useStore } from '../store/store';
import { getEnvUrl } from '../helpers/getEnvUrl';
import { applyFoodzeeAuthTokens } from '../helpers/applyFoodzeeAuthTokens';
import { buildAnonEmail } from '../helpers/anonEmail';

const STRIPE_PAYMENT_SYSTEM = 'stripe';
const TRUEGATE_PAYMENT_SYSTEM = 'truegate';

/**
 * Deferred-email flow: when `collectEmailAfterPayment` is enabled, create an
 * anonymous user when the user reaches the paywall / personal-plan page, so
 * checkout needs no email — which lifts conversion. The email is collected after
 * payment on the dedicated `email-after-payment` page via `/user/update-by-token`.
 *
 * Two payment systems, one anonymous user, different billing wiring:
 *  - Stripe:   the user is created with `email = null` and its token also
 *              authorizes billing, so the tokens are synced and billing re-inits.
 *  - Truegate: billing is authorized separately against recurrentbusiness by the
 *              `auth()` thunk, so the Voery token must NOT be pushed into billing
 *              — it would clobber that auth. And since the truegate backend keys
 *              its customer by email, we seed the profile with a synthetic
 *              `anon-<uid>@voery.app` address so order creation can proceed.
 */
export function useAnonymousUser(isRemoteConfigLoaded: boolean = true) {
  const dispatch = useDispatch();
  // @ts-ignore — collectEmailAfterPayment lives on the core RemoteConfig type
  const { paymentSystem, collectEmailAfterPayment } = useRemoteConfig();

  const userId = useStore((state) => state.userId);
  const authTokens = useStore((state) => state.authTokens);
  const setUserId = useStore((state) => state.setUserId);

  const requestedRef = useRef(false);
  const { apiBaseUrl } = getEnvUrl();

  useEffect(() => {
    if (!isRemoteConfigLoaded) return;

    const isStripe = paymentSystem === STRIPE_PAYMENT_SYSTEM;
    const isTruegate = paymentSystem === TRUEGATE_PAYMENT_SYSTEM;

    if ((!isStripe && !isTruegate) || !collectEmailAfterPayment) return;
    if (requestedRef.current) return;

    // Truegate only: its billing customer is keyed by email and order creation
    // fails without one, so seed the profile with the synthetic address derived
    // from the uid. Stripe charges with email = null and needs nothing here.
    const seedAnonEmail = (uid?: string) => {
      if (isTruegate && uid) {
        dispatch(setProfileEmail(buildAnonEmail(uid)));
      }
    };

    // Returning visitor already has a user — re-apply persisted tokens so billing
    // is initialized, but don't create a second anonymous user.
    if (userId || authTokens?.accessToken) {
      requestedRef.current = true;
      seedAnonEmail(userId || authTokens?.userId);
      if (authTokens?.accessToken) {
        applyFoodzeeAuthTokens(
          {
            accessToken: authTokens.accessToken,
            refreshToken: authTokens.refreshToken,
            uid: authTokens.userId || userId,
          },
          dispatch,
          { reinitBilling: true, paymentSystem, syncBillingAuth: isStripe }
        );
      }
      return;
    }

    requestedRef.current = true;

    (async () => {
      try {
        const response = await axios.post(`${apiBaseUrl}/v2/user`);

        if (response.status === 201) {
          setUserId(response.data.uid);
          seedAnonEmail(response.data.uid);
          // syncBillingAuth stays off for truegate: billing there is authorized
          // against recurrentbusiness by the `auth()` thunk, and pushing the Voery
          // token into Billing would replace that auth and break order creation.
          applyFoodzeeAuthTokens(response.data, dispatch, {
            reinitBilling: true,
            paymentSystem,
            syncBillingAuth: isStripe,
          });
          // Truegate only: the page's own `init()` already ran (it fires as soon
          // as billing auth is ready, which is before this request resolves), so
          // its `initOrders` read an empty profile email and skipped both the
          // customer and the order. Nothing replays it afterwards — for truegate
          // `applyFoodzeeAuthTokens` returns before honouring `reinitBilling`,
          // and `init()` is memoized on an apiAuthKey that doesn't change here.
          // Re-run it now that the synthetic email is in the store.
          if (isTruegate) {
            // @ts-ignore
            dispatch(initOrders());
          }
          Analytics.trackEvent('anonymous_user', 'created');
        }
      } catch (error) {
        // Allow a retry on the next render if the request failed.
        requestedRef.current = false;
        Analytics.trackEvent('anonymous_user', 'error');
      }
    })();
  }, [
    apiBaseUrl,
    authTokens,
    collectEmailAfterPayment,
    dispatch,
    isRemoteConfigLoaded,
    paymentSystem,
    setUserId,
    userId,
  ]);
}
